import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, MessageSquareWarning, Inbox, Loader2 } from 'lucide-react';
import { API, errText, fileUrl } from '../../api';
import RichText from '../RichText';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Date TBD');
const STATUS = { pending: ['Needs your OK', 'text-gold border-gold/40'], approved: ['Approved', 'text-emerald-400 border-emerald-500/40'], changes: ['Changes requested', 'text-heart border-heart/40'], posted: ['Posted', 'text-cream/50 border-cream/20'] };

export function PostMedia({ post, className = '' }) {
  if (!post.media_url) return null;
  const src = fileUrl(post.media_url);
  return (post.media_type || '').startsWith('video')
    ? <video src={src} controls preload="metadata" className={`rounded-xl bg-black ${className}`} />
    : <img src={src} alt="" className={`rounded-xl object-cover ${className}`} />;
}

export default function ApprovalInbox({ onPosts }) {
  const [posts, setPosts] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => API.posts().then((p) => { setPosts(p); onPosts?.(p); }).catch(() => setPosts([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!posts || !posts.length) return null;

  const review = async (p, decision) => {
    if (decision === 'changes' && !(feedback[p.id] || '').trim()) { toast.error('Tell Perry what to change.'); return; }
    setBusy(p.id);
    try { await API.reviewPost(p.id, decision, feedback[p.id] || ''); toast.success(decision === 'approved' ? 'Approved — Perry will post it.' : 'Sent to Perry.'); await load(); }
    catch (e) { toast.error(errText(e, 'Could not send.')); }
    finally { setBusy(null); }
  };
  const pending = posts.filter((p) => p.status === 'pending');

  return (
    <div className="panel rounded-3xl p-6" data-testid="approval-inbox">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-display text-2xl text-cream"><Inbox size={18} className="text-gold" /> Posts for your approval</p>
        {pending.length > 0 && <span className="text-[10px] tracking-widest uppercase bg-gold text-ink rounded-full px-2 py-0.5" data-testid="approval-pending-count">{pending.length} waiting</span>}
      </div>
      <p className="text-xs text-cream/50 mt-1">Perry prepares each post; nothing goes out until you approve it here.</p>
      <div className="mt-4 space-y-4">
        {posts.map((p) => (
          <div key={p.id} className="rounded-2xl border hairline p-4" data-testid={`post-${p.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-cream">{p.platform} &middot; <span className="text-cream/55">{fmtDate(p.scheduled_for)}</span></p>
              <span className={`text-[10px] tracking-widest uppercase border rounded-full px-2 py-0.5 ${STATUS[p.status]?.[1]}`} data-testid={`post-status-${p.id}`}>{STATUS[p.status]?.[0] || p.status}</span>
            </div>
            <div className="mt-3 grid sm:grid-cols-[160px_1fr] gap-4">
              <PostMedia post={p} className="w-full max-h-48" />
              <div className="text-sm text-cream/80 leading-relaxed"><RichText text={p.caption} /></div>
            </div>
            {p.status === 'changes' && p.feedback && <p className="mt-3 text-xs text-cream/55">Your note: {p.feedback}</p>}
            {p.status === 'pending' && (
              <div className="mt-4 space-y-2">
                <textarea value={feedback[p.id] || ''} onChange={(e) => setFeedback({ ...feedback, [p.id]: e.target.value })} rows={2} placeholder="Optional note for Perry (required if you ask for changes)" data-testid={`post-feedback-${p.id}`} className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-2.5 text-sm text-cream outline-none focus:border-gold/50 resize-y" />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => review(p, 'changes')} disabled={busy === p.id} data-testid={`post-changes-${p.id}`} className="btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-50"><MessageSquareWarning size={13} /> Request changes</button>
                  <button onClick={() => review(p, 'approved')} disabled={busy === p.id} data-testid={`post-approve-${p.id}`} className="btn-gold rounded-full px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-50">{busy === p.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Approve</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
